import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints, ApiError, type FetchOutcome, type ReleaseTrack, type CommentsResponse } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/spinner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const MINT = "#aaffdc";
const CYAN = "#81ecff";
const PURPLE = "#c57eff";

// Match nox-api smartlink renderer's PLATFORM_LABELS exactly.
const PRESETS = [
  { key: "spotify", label: "Spotify" },
  { key: "apple", label: "Apple Music" },
  { key: "beatport", label: "Beatport" },
  { key: "bandcamp", label: "Bandcamp" },
  { key: "soundcloud", label: "SoundCloud" },
  { key: "mixcloud", label: "Mixcloud" },
  { key: "youtube", label: "YouTube" },
  { key: "tidal", label: "Tidal" },
  { key: "deezer", label: "Deezer" },
];

export default function ReleaseDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { active } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["release", active?.slug, slug],
    queryFn: () => endpoints.getRelease(active!.slug, slug!),
    enabled: !!active && !!slug,
  });

  const history = useQuery({
    queryKey: ["statsHistory", active?.slug, slug],
    queryFn: () => endpoints.statsHistory(active!.slug, slug!),
    enabled: !!active && !!slug,
  });
  const comments = useQuery({
    queryKey: ["comments", active?.slug, slug],
    queryFn: () => endpoints.comments(active!.slug, slug!),
    enabled: !!active && !!slug,
  });

  if (isLoading) return <div className="p-6"><Spinner /></div>;
  if (error) return <div className="p-6"><p className="text-sm text-destructive">{(error as Error).message}</p></div>;
  if (!data) return null;

  const r = data.release;
  const releaseArtwork = r.artwork_url ?? data.tracks.find((t) => t.artwork_url)?.artwork_url ?? null;

  return (
    <div className="p-6 flex flex-col gap-6">
      {/* Release header card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <ArtworkBlock artwork={releaseArtwork} />
              <div>
                <CardTitle>{r.title}</CardTitle>
                <CardDescription>
                  {[r.artist, r.catalog_no, r.release_date].filter(Boolean).join(" · ") || "—"}
                </CardDescription>
                <div className="mt-2 flex items-center gap-2">
                  <StageBadge stage={r.stage} />
                  {data.smartlinkUrl && (
                    <a
                      href={data.smartlinkUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ backgroundColor: "#262626", color: PURPLE, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}
                      className="hover:underline"
                    >
                      live ↗
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to={`/app/releases/${r.slug ?? r.id}/dj-targets`}>
                <Button
                  size="sm"
                  style={{ backgroundColor: PURPLE, color: "#000" }}
                  className="hover:opacity-90"
                >
                  Rank DJs
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tracks */}
      <TracksCard data={data} />

      {/* Smartlink */}
      <SmartlinkCard data={data} />

      {/* Engagement + Destinations */}
      <EngagementCard data={data} />

      {/* Plays over time */}
      <VelocityChartCard history={history.data} />

      {/* Platform stats */}
      <PlatformStatsCard data={data} history={history.data} />

      {/* Comments */}
      <CommentsCard data={comments.data} signalCount={data.djSignalCount} />
    </div>
  );
}

// ─── Header pieces ──────────────────────────────────────────────────────────
function ArtworkBlock({ artwork }: { artwork: string | null }) {
  return (
    <div
      className="rounded-md overflow-hidden border border-border bg-muted shrink-0"
      style={{ width: 80, height: 80, minWidth: 80, minHeight: 80 }}
    >
      {artwork && (
        <img
          src={artwork}
          alt=""
          className="block w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
      )}
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  if (stage === "promo") {
    return (
      <span style={{ backgroundColor: "#262626", color: MINT, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
        promo
      </span>
    );
  }
  if (stage === "released") {
    return <Badge variant="purple">released</Badge>;
  }
  return <Badge variant="secondary">{stage}</Badge>;
}

// ─── Tracks card ────────────────────────────────────────────────────────────
function TracksCard({ data }: { data: import("../api").ReleaseDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Tracks</CardTitle>
        <CardDescription>
          {data.tracks.length === 0
            ? "No tracks attached yet — upload via NOX Private."
            : `${data.tracks.length} ${data.tracks.length === 1 ? "track" : "tracks"} on this release.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data.tracks.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Artist</TableHead>
                <TableHead>BPM</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Length</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.tracks.map((t, i) => <TrackRow key={t.track_id} t={t} index={i + 1} />)}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function TrackRow({ t, index }: { t: ReleaseTrack; index: number }) {
  const length = t.duration_seconds != null
    ? `${Math.floor(t.duration_seconds / 60)}:${String(t.duration_seconds % 60).padStart(2, "0")}`
    : "—";
  return (
    <TableRow>
      <TableCell>
        <div
          className="rounded overflow-hidden border border-border bg-muted shrink-0"
          style={{ width: 36, height: 36, minWidth: 36, minHeight: 36 }}
        >
          {t.artwork_url && (
            <img
              src={t.artwork_url}
              alt=""
              className="block w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
            />
          )}
        </div>
      </TableCell>
      <TableCell><span style={{ color: "#adaaaa" }}>{t.track_number ?? String(index).padStart(2, "0")}</span></TableCell>
      <TableCell className="font-normal">{t.title}</TableCell>
      <TableCell><span style={{ color: "#adaaaa" }}>{t.artist ?? "—"}</span></TableCell>
      <TableCell>
        {t.bpm != null ? (
          <span style={{ backgroundColor: "#262626", color: CYAN, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 700 }} className="tabular-nums">
            {t.bpm}
          </span>
        ) : "—"}
      </TableCell>
      <TableCell>
        {t.musical_key ? (
          <span style={{ backgroundColor: "#262626", color: PURPLE, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 700 }} className="tabular-nums">
            {t.musical_key}
          </span>
        ) : "—"}
      </TableCell>
      <TableCell className="tabular-nums">{length}</TableCell>
    </TableRow>
  );
}

// ─── Smartlink card ─────────────────────────────────────────────────────────
function SmartlinkCard({ data }: { data: import("../api").ReleaseDetail }) {
  const { active } = useAuth();
  const qc = useQueryClient();
  const slugForUrl = data.release.slug ?? data.release.id;
  const [showForm, setShowForm] = useState(!data.smartlinkUrl);
  const [copied, setCopied] = useState(false);

  // Existing destinations parsed from the latest smartlink row (if any).
  // Used to seed the edit form so users see what they currently have set.
  const existing = useMemo(() => {
    const raw = data.smartlinks[0]?.destinations_json;
    if (!raw) return {} as Record<string, string>;
    try {
      const parsed = JSON.parse(raw) as Array<{ platform: string; url: string }>;
      return parsed.reduce<Record<string, string>>((acc, d) => {
        if (d.platform && d.url) acc[d.platform] = d.url;
        return acc;
      }, {});
    } catch { return {}; }
  }, [data.smartlinks]);

  const [urls, setUrls] = useState<Record<string, string>>(existing);
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    // Pre-tick anything the user already has, plus the four common defaults
    // for first-time setup. PRESETS is the full list of supported platforms.
    const initial: Record<string, boolean> = { spotify: true, apple: true, beatport: true, bandcamp: true };
    for (const k of Object.keys(existing)) initial[k] = true;
    return initial;
  });

  // When the upstream data changes (e.g. after publish completes and re-fetches),
  // re-seed the form so the next "Edit destinations" click shows current values
  // — not whatever the user typed before publishing.
  useEffect(() => {
    setUrls(existing);
    setEnabled((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(existing)) next[k] = true;
      return next;
    });
  }, [existing]);

  const create = useMutation({
    mutationFn: (destinations: { platform: string; url: string }[]) =>
      endpoints.createSmartlink(active!.slug, slugForUrl, destinations),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["release", active?.slug, slugForUrl] });
      setShowForm(false);
    },
  });

  const copy = async () => {
    if (!data.smartlinkUrl) return;
    await navigator.clipboard.writeText(data.smartlinkUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Smartlink</CardTitle>
            <CardDescription>Public landing page with first-party pixel + opt-in capture.</CardDescription>
          </div>
          {data.smartlinkUrl && !showForm && (
            <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>Edit destinations</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {data.smartlinkUrl && !showForm && (
          <div className="rounded-md p-4 border" style={{ backgroundColor: "rgba(197,126,255,0.06)", borderColor: "rgba(197,126,255,0.18)" }}>
            <a href={data.smartlinkUrl} target="_blank" rel="noreferrer" className="text-sm font-mono break-all hover:underline" style={{ color: PURPLE }}>
              {data.smartlinkUrl}
            </a>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={copy}>{copied ? "Copied" : "Copy"}</Button>
              <a href={data.smartlinkUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline">Open ↗</Button>
              </a>
            </div>
          </div>
        )}

        {(showForm || !data.smartlinkUrl) && (
          <div className="space-y-2">
            {PRESETS.map((p) => (
              <div key={p.key} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!enabled[p.key]}
                  onChange={(e) => setEnabled({ ...enabled, [p.key]: e.target.checked })}
                  className="w-4 h-4"
                  style={{ accentColor: PURPLE }}
                />
                <span className="w-24 text-sm font-normal">{p.label}</span>
                <Input
                  placeholder={`https://${p.key}.com/…`}
                  value={urls[p.key] ?? ""}
                  onChange={(e) => setUrls({ ...urls, [p.key]: e.target.value })}
                  disabled={!enabled[p.key]}
                  className="flex-1"
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              {data.smartlinkUrl && (
                <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              )}
              <Button
                disabled={create.isPending}
                onClick={() => {
                  const dests = PRESETS
                    .filter((p) => enabled[p.key] && (urls[p.key] ?? "").trim() !== "")
                    .map((p) => ({ platform: p.key, url: urls[p.key].trim() }));
                  create.mutate(dests);
                }}
              >
                {create.isPending ? "Publishing…" : "Publish smartlink"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Engagement + destinations ──────────────────────────────────────────────
function EngagementCard({ data }: { data: import("../api").ReleaseDetail }) {
  const events = data.counts;
  const eventTotal = Object.values(events).reduce((a: number, b) => a + (b as number), 0);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Engagement</CardTitle>
          <CardDescription>{eventTotal === 0 ? "No pixel events yet." : `${eventTotal} events on the public smartlink.`}</CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(events).length === 0 ? (
            <p className="text-sm text-muted-foreground">Once your smartlink is published, page views and click-outs land here.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(events).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell><span style={{ color: "#adaaaa" }}>{k.replace(/_/g, " ")}</span></TableCell>
                    <TableCell className="text-right tabular-nums font-normal">{(v as number).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Destination clicks</CardTitle>
          <CardDescription>Click-outs to each DSP from the smartlink.</CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(data.destinations).length === 0 ? (
            <p className="text-sm text-muted-foreground">No clicks yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destination</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(data.destinations).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell className="capitalize">{k}</TableCell>
                    <TableCell className="text-right tabular-nums font-normal">{v.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Platform stats card ────────────────────────────────────────────────────
function PlatformStatsCard({
  data,
  history,
}: {
  data: import("../api").ReleaseDetail;
  history: import("../api").StatsHistoryResponse | undefined;
}) {
  const { active } = useAuth();
  const qc = useQueryClient();
  const slugForUrl = data.release.slug ?? data.release.id;
  const [err, setErr] = useState<string | null>(null);

  const refresh = useMutation<{ outcomes: FetchOutcome[] }, Error, void>({
    mutationFn: () => endpoints.fetchStats(active!.slug, slugForUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["release", active?.slug, slugForUrl] });
      qc.invalidateQueries({ queryKey: ["statsHistory", active?.slug, slugForUrl] });
      qc.invalidateQueries({ queryKey: ["comments", active?.slug, slugForUrl] });
      setErr(null);
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  // Spotify uses a 0–100 popularity score, not real plays — segregate it from
  // the plays-based table and chart, surface as a standalone stat.
  const spotifyStat = data.platformStats.find((s) => s.platform === "spotify");
  const playStats = data.platformStats.filter((s) => s.platform !== "spotify");

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Platform stats</CardTitle>
            <CardDescription>SoundCloud · Mixcloud · YouTube · Bandcamp</CardDescription>
          </div>
          <Button size="sm" variant="outline" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? "Polling…" : "Refresh"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {err && <p className="text-sm text-destructive mb-3">{err}</p>}

        {spotifyStat && spotifyStat.plays != null && (
          <div className="mb-4 rounded-lg border border-border p-3 flex items-center gap-3" style={{ backgroundColor: "#1e1e1e" }}>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: "#adaaaa" }}>Spotify popularity</span>
            <span className="tabular-nums text-base" style={{ color: MINT }}>{spotifyStat.plays}</span>
            <span className="text-xs" style={{ color: "#666" }}>/ 100</span>
            <span className="ml-auto text-[11px]" style={{ color: "#adaaaa" }}>
              Spotify's public API doesn't expose stream counts — popularity (0–100) is a rolling proxy
            </span>
          </div>
        )}

        {playStats.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Add a SoundCloud / YouTube / Bandcamp URL to your smartlink, then refresh.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Platform</TableHead>
                <TableHead>Plays</TableHead>
                <TableHead>Last 7 days</TableHead>
                <TableHead>Likes</TableHead>
                <TableHead>Trend</TableHead>
                <TableHead>Fetched</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {playStats.map((s, i) => {
                const baseline = data.platformBaseline.find((b) => b.platform === s.platform && b.external_url === s.external_url);
                const delta = baseline?.plays != null && s.plays != null ? s.plays - baseline.plays : null;
                const series = history?.byPlatform[s.platform]?.map((p) => p.plays).filter((p): p is number => p != null) ?? [];
                return (
                  <TableRow key={i}>
                    <TableCell className="capitalize font-normal">{s.platform}</TableCell>
                    <TableCell className="tabular-nums">{s.plays?.toLocaleString() ?? "—"}</TableCell>
                    <TableCell className="tabular-nums">
                      {delta == null ? <span className="text-muted-foreground">—</span> : (
                        <span style={{ color: delta > 0 ? MINT : delta < 0 ? "#ff6b6b" : "#adaaaa" }}>
                          {delta > 0 ? "+" : ""}{delta.toLocaleString()}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums"><span style={{ color: "#adaaaa" }}>{s.likes?.toLocaleString() ?? "—"}</span></TableCell>
                    <TableCell><Sparkline values={series} /></TableCell>
                    <TableCell><span style={{ color: "#adaaaa" }}>{s.fetched_at?.slice(0, 16) ?? "—"}</span></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Sparkline({ values, width = 80, height = 22 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return <span className="text-muted-foreground text-xs">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * (height - 4) - 2).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <polyline points={points} fill="none" stroke={CYAN} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── Comments card ──────────────────────────────────────────────────────────
function CommentsCard({ data, signalCount }: { data: CommentsResponse | undefined; signalCount: number }) {
  const [showAll, setShowAll] = useState(false);
  if (!data) return null;
  const list = showAll ? data.recent : data.signals;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Comments</CardTitle>
            <CardDescription>
              {signalCount > 0 ? `${signalCount} DJ-signal · ${data.recent.length} recent` : `${data.recent.length} recent comments`}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={!showAll ? "default" : "outline"} onClick={() => setShowAll(false)}>Signals</Button>
            <Button size="sm" variant={showAll ? "default" : "outline"} onClick={() => setShowAll(true)}>All</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">{showAll ? "No comments yet — refresh stats." : "No DJ-signal comments detected yet."}</p>
        ) : (
          <div className="space-y-3">
            {list.map((c) => (
              <div key={c.id} className="rounded-lg border border-border p-3" style={{ backgroundColor: "#1e1e1e" }}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-normal" style={{ color: c.is_dj_signal ? PURPLE : undefined }}>@{c.author ?? "anon"}</span>
                    <span className="ml-2 text-xs uppercase tracking-wider" style={{ color: "#adaaaa" }}>{c.platform}</span>
                  </div>
                  <span className="text-xs" style={{ color: "#adaaaa" }}>{c.posted_at?.slice(0, 16)}</span>
                </div>
                <p className="text-sm mt-1 whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Velocity chart ─────────────────────────────────────────────────────────
// Mirrors the nox dashboard's analytics velocity chart: AreaChart with NOX
// palette gradient fills, no axis lines, gray ticks. Deterministic colour per
// platform so SoundCloud is always mint, Mixcloud always purple, etc.
const PLATFORM_COLORS: Record<string, string> = {
  soundcloud: MINT,
  mixcloud: PURPLE,
  bandcamp: CYAN,
  youtube: "#fbbf24", // amber, matches Badge variant=amber
};

function VelocityChartCard({ history }: { history: import("../api").StatsHistoryResponse | undefined }) {
  const points = buildChartPoints(history);
  const rows = points.rows;
  const platforms = points.platforms;

  // Data is aggregated per UTC day in buildChartPoints, so every tick is a
  // distinct date. UTC formatting matches the bucketing — without it, a
  // user in UTC+10 sees the bucket as "the previous day" near midnight.
  const formatTick = (v: string) => {
    const d = new Date(v);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  };
  const colorFor = (p: string) => PLATFORM_COLORS[p] ?? MINT;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Velocity</CardTitle>
        <CardDescription>Plays gained per day, by platform</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">No history yet — refresh stats to populate.</p>
        ) : rows.length === 1 ? (
          <p className="text-sm text-muted-foreground py-12 text-center">
            Need at least 2 days of polling to show a velocity trend. The daily poller runs overnight — check back tomorrow.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={rows} margin={{ top: 16, right: 4, left: -16, bottom: 0 }}>
              <defs>
                {platforms.map((p) => (
                  <linearGradient key={p} id={`g_${p}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colorFor(p)} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={colorFor(p)} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <XAxis
                dataKey="ts"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#adaaaa" }}
                tickFormatter={formatTick}
                // minTickGap thins labels so wider "MMM dd HH:mm" strings
                // don't overlap. Recharts picks ticks that fit; with a 60px
                // gap the chart settles on ~4-6 visible labels at any width.
                minTickGap={60}
              />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#adaaaa" }} width={36} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1a1919",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 8,
                  fontSize: 11,
                  color: "#fff",
                }}
                labelFormatter={(v) => {
                  const d = new Date(String(v));
                  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
                }}
              />
              <Legend
                verticalAlign="top"
                align="right"
                iconType="circle"
                iconSize={6}
                wrapperStyle={{ fontSize: 10, color: "#adaaaa", paddingBottom: 8 }}
              />
              {platforms.map((p) => {
                // Show dots when a series has fewer than 4 points so a single
                // snapshot or sparse polling is still visible. Recharts' dot
                // prop on a per-series basis is the simplest way to do this.
                const seriesCount = rows.filter((row) => row[p] != null).length;
                const showDots = seriesCount < 4;
                return (
                  <Area
                    key={p}
                    // `monotone` matches the dashboard's analytics chart and
                    // produces clean arc curves when the data has real
                    // peaks/troughs — which it does now that we plot daily
                    // deltas (plays gained per day) instead of cumulative
                    // counters that were near-flat at this scale.
                    type="monotone"
                    dataKey={p}
                    name={p}
                    stroke={colorFor(p)}
                    strokeWidth={2}
                    fill={`url(#g_${p})`}
                    dot={showDots ? { r: 3, fill: colorFor(p), stroke: colorFor(p) } : false}
                    activeDot={{ r: 4, fill: colorFor(p) }}
                    connectNulls
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

function buildChartPoints(history: import("../api").StatsHistoryResponse | undefined) {
  if (!history) return { rows: [], platforms: [] };
  // Spotify lives on a 0–100 popularity score, not real stream counts. On the
  // same Y axis as Mixcloud/Bandcamp/SoundCloud plays it just looks like a flat
  // line. Excluded — surfaced as a standalone stat in PlatformStatsCard instead.
  const entries = Object.entries(history.byPlatform).filter(
    ([platform, pts]) => platform !== "spotify" && pts.length > 0
  );
  if (entries.length === 0) return { rows: [], platforms: [] };

  // Show every play-tracking platform (SC / Mixcloud / Bandcamp / YouTube).
  // The set is small enough that a top-N cap was over-engineering and just
  // dropped real signal from the chart.
  const ranked = entries
    .map(([platform, pts]) => ({ platform, max: Math.max(...pts.map((p) => p.plays ?? 0)) }))
    .sort((a, b) => b.max - a.max)
    .map((x) => x.platform);

  // Aggregate per (platform, day) so the chart shows one point per day,
  // not one per poll. Plays counters are cumulative + monotonic (each poll
  // returns the running total from the platform), so MAX-of-day = the
  // best end-of-day reading. The chart's `ts` becomes a UTC midnight
  // timestamp so day boundaries align cleanly across platforms.
  const dailyMax = new Map<string, Map<string, number>>(); // platform → (day → maxPlays)
  for (const p of ranked) {
    const perDay = new Map<string, number>();
    for (const pt of history.byPlatform[p]) {
      if (pt.plays == null) continue;
      const day = pt.ts.slice(0, 10); // YYYY-MM-DD
      const cur = perDay.get(day);
      if (cur == null || pt.plays > cur) perDay.set(day, pt.plays);
    }
    dailyMax.set(p, perDay);
  }

  const dayList = Array.from(
    new Set(Array.from(dailyMax.values()).flatMap((m) => Array.from(m.keys()))),
  ).sort();

  // Convert cumulative max-of-day → plays GAINED that day. The polled
  // counters from SoundCloud / Mixcloud / Bandcamp are running totals,
  // which look near-flat on a chart even when a track is gaining real
  // velocity. Deltas surface actual day-to-day activity — a true
  // velocity view, matching the dashboard's streams-per-day shape.
  //
  // Missing data backfills to 0 rather than null:
  //   - Day 1 has no prior baseline (we'd be subtracting from "before time
  //     began"), so anchor it to 0. Without this the line starts mid-air
  //     on day 2.
  //   - A platform with no poll on a given day also reads as 0. Slight
  //     overstatement of "no plays" vs the more accurate "we didn't poll",
  //     but the chart stays readable instead of floating across gaps.
  //   - Negative deltas (a counter regressing — network noise, platform
  //     dedup) are floored at 0 so the chart can't dip below the X axis.
  const rows = dayList.map((day, i) => {
    const row: Record<string, string | number | null> = { ts: `${day}T00:00:00Z` };
    for (const p of ranked) {
      const today = dailyMax.get(p)?.get(day);
      if (i === 0 || today == null) {
        row[p] = 0;
        continue;
      }
      const prevDay = dayList[i - 1];
      const prev = dailyMax.get(p)?.get(prevDay);
      row[p] = prev == null ? 0 : Math.max(0, today - prev);
    }
    return row;
  });

  return { rows, platforms: ranked };
}

